'use client';

/**
 * Authentication Context
 * Provides authentication state and methods throughout the application.
 * Supports both web (cookie-based) and native (PKCE + Bearer token) auth.
 */

import React, { createContext, useContext, useEffect, useState, useCallback } from 'react';
import { isNative } from '@/lib/platform';

export interface User {
  sub: string;
  email?: string;
  email_verified?: boolean;
  name?: string;
  picture?: string;
  nickname?: string;
  updated_at?: string;
}

interface AuthState {
  user: User | null;
  isAuthenticated: boolean;
  isLoading: boolean;
  error: string | null;
  csrfToken: string | null;
}

export interface LoginOptions {
  returnTo?: string;
  screenHint?: 'signup' | 'login';
}

interface AuthContextValue extends AuthState {
  login: (options?: LoginOptions) => void;
  logout: () => void;
  refresh: () => Promise<void>;
  /**
   * Make an authenticated fetch request with CSRF token included
   * Use this for all POST/PUT/PATCH/DELETE requests to /api/secure/*
   */
  secureFetch: (url: string, options?: RequestInit) => Promise<Response>;
}

const AuthContext = createContext<AuthContextValue | undefined>(undefined);

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [state, setState] = useState<AuthState>({
    user: null,
    isAuthenticated: false,
    isLoading: true,
    error: null,
    csrfToken: null,
  });

  /**
   * Fetch CSRF token for authenticated requests
   */
  const fetchCsrfToken = useCallback(async (): Promise<string | null> => {
    try {
      const response = await fetch('/api/secure/csrf-token', {
        credentials: 'include',
      });

      if (!response.ok) {
        console.warn('Failed to fetch CSRF token');
        return null;
      }

      const data = await response.json();
      return data.csrfToken || null;
    } catch (error) {
      console.error('Failed to fetch CSRF token:', error);
      return null;
    }
  }, []);

  /**
   * Fetch current user from API
   */
  const fetchUser = useCallback(async () => {
    try {
      setState(prev => ({ ...prev, isLoading: true, error: null }));

      const response = await fetch('/api/auth/me', {
        credentials: 'include',
      });

      if (!response.ok) {
        throw new Error('Failed to fetch user');
      }

      const data = await response.json();

      // Fetch CSRF token if authenticated
      let csrfToken: string | null = null;
      if (data.authenticated) {
        csrfToken = await fetchCsrfToken();
      }

      setState({
        user: data.user,
        isAuthenticated: data.authenticated,
        isLoading: false,
        error: null,
        csrfToken,
      });
    } catch (error) {
      console.error('Failed to fetch user:', error);
      setState({
        user: null,
        isAuthenticated: false,
        isLoading: false,
        error: error instanceof Error ? error.message : 'Failed to fetch user',
        csrfToken: null,
      });
    }
  }, [fetchCsrfToken]);

  /**
   * Initialize authentication state
   */
  useEffect(() => {
    fetchUser();
  }, [fetchUser]);

  /**
   * Login - native PKCE flow or web redirect
   */
  const login = useCallback(async (options?: LoginOptions) => {
    if (isNative()) {
      try {
        const { nativeLogin } = await import('./NativeAuthProvider');
        await nativeLogin();
      } catch (error) {
        console.error('[Auth] Native login failed:', error);
      }
      return;
    }

    const params = new URLSearchParams();
    if (options?.returnTo) {
      params.set('returnTo', options.returnTo);
    }
    if (options?.screenHint) {
      params.set('screenHint', options.screenHint);
    }
    window.location.href = `/api/auth/login${params.toString() ? `?${params.toString()}` : ''}`;
  }, []);

  /**
   * Logout - native token clear or web redirect
   */
  const logout = useCallback(async () => {
    if (isNative()) {
      try {
        const { nativeLogout } = await import('./NativeAuthProvider');
        await nativeLogout();
        setState({
          user: null,
          isAuthenticated: false,
          isLoading: false,
          error: null,
          csrfToken: null,
        });
      } catch (error) {
        console.error('[Auth] Native logout failed:', error);
      }
      return;
    }

    window.location.href = '/api/auth/logout';
  }, []);

  /**
   * Refresh user data
   */
  const refresh = useCallback(async () => {
    await fetchUser();
  }, [fetchUser]);

  /**
   * Make a secure fetch request with CSRF token
   * Automatically includes credentials and CSRF token header
   *
   * For state-changing requests (POST, PUT, PATCH, DELETE):
   * - Always fetches a fresh CSRF token to ensure cookie and header match
   * - This prevents issues with stale tokens (1hr expiry) in long-lived sessions
   */
  const secureFetch = useCallback(async (url: string, options: RequestInit = {}): Promise<Response> => {
    const headers = new Headers(options.headers);

    // Include CSRF token for state-changing requests
    const method = options.method?.toUpperCase() || 'GET';
    if (['POST', 'PUT', 'PATCH', 'DELETE'].includes(method)) {
      // Always fetch a fresh token to ensure cookie and header are in sync
      // This handles cases where:
      // 1. The cookie has expired (1hr MaxAge)
      // 2. The page was open for a long time
      // 3. The initial token fetch failed
      const freshToken = await fetchCsrfToken();
      if (freshToken) {
        headers.set('X-CSRF-Token', freshToken);
        setState(prev => ({ ...prev, csrfToken: freshToken }));
      } else {
        console.error('[Auth] Failed to obtain CSRF token for secure request');
      }
    }

    return fetch(url, {
      ...options,
      headers,
      credentials: 'include',
    });
  }, [fetchCsrfToken]);

  const value: AuthContextValue = {
    ...state,
    login,
    logout,
    refresh,
    secureFetch,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

/**
 * Hook to use authentication context
 */
export function useAuth(): AuthContextValue {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
}
